import type { ErrorObject } from "ajv";
import schemas from "./schemas.json" with { type: "json" };
import { apiValidators } from "./generated-validators.js";
import type { PrecompiledValidator } from "./generated-validators.js";

/** Canonical transport schemas; never import these into a presentation scene. */
export const apiSchemas = schemas;
export type ApiSchemaName = keyof typeof schemas;

function runtimeReferences(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(runtimeReferences);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [
      key,
      key === "$ref" && typeof item === "string"
        ? item.replace("#/components/schemas/", "#/$defs/")
        : runtimeReferences(item),
    ]));
  }
  return value;
}

const definitions = runtimeReferences(schemas) as Record<string, object>;

/** A self-contained draft-2020-12 schema, including its referenced definitions. */
export function apiSchema(name: ApiSchemaName): object {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $ref: `#/$defs/${name}`,
    $defs: definitions,
  };
}

// This module also runs in the browser, where `script-src 'self'` forbids the
// Function() call of a run-time schema compiler. generate.mjs compiles every
// schema ahead of time; the run-time compiler lives in the server-only compile.ts.
const validators: Readonly<Record<ApiSchemaName, PrecompiledValidator>> = apiValidators;

function getValidator(name: ApiSchemaName): PrecompiledValidator {
  // Untyped callers must not reach an inherited property such as toString.
  if (!Object.prototype.hasOwnProperty.call(validators, name)) throw new Error(`Unknown API contract schema ${String(name)}.`);
  return validators[name];
}

export function validateApiValue(name: ApiSchemaName, value: unknown): boolean {
  return getValidator(name)(value);
}

export class ApiContractValidationError extends Error {
  readonly schemaName: ApiSchemaName;
  readonly issues: readonly ErrorObject[];

  constructor(schemaName: ApiSchemaName, issues: readonly ErrorObject[]) {
    super(`Value does not match the ${schemaName} API contract.`);
    this.name = "ApiContractValidationError";
    this.schemaName = schemaName;
    // Diagnostics contain schema paths and constraints, never the rejected data.
    this.issues = issues;
  }
}

export function assertApiValue(name: ApiSchemaName, value: unknown): void {
  const validate = getValidator(name);
  if (!validate(value)) {
    throw new ApiContractValidationError(name, [...(validate.errors ?? [])]);
  }
}
